import { DOCUMENT } from '@angular/common';
import { inject, Injectable, Injector } from '@angular/core';
import { ENVIRONMENT_CONFIG } from '@core/config-angular';
import type { ClientEnvironmentConfig } from '@core/config-models';
import { getPageTitle } from '@core/jss-routing';
import { select, Store } from '@ngrx/store';
import { filter, firstValueFrom, map, take } from 'rxjs';

import { formatUrlAsPageName } from './page-name';

@Injectable({
  providedIn: 'root',
})
export class AnalyticsUtils {
  readonly #document = inject(DOCUMENT);
  readonly #store$ = inject(Store);
  readonly #injector = inject(Injector);

  private readonly location = this.#document.location;

  private _config?: ClientEnvironmentConfig;

  // Cache for site config. Improves performance by reducing injector lookups.
  #config() {
    if (!this._config) {
      this._config = this.#injector.get(ENVIRONMENT_CONFIG);
    }
    return this._config;
  }

  /**
   * transforms the current document URL to a `:` delimited format.
   * e.g. https://www.essent.nl/isolatie -> www:essent:isolatie
   * @returns name of the currently active document in a ':' delimited format.
   */
  public getPageName() {
    return formatUrlAsPageName(this.#document.URL);
  }

  /**
   * transforms the document location to a URL without query params and fragments.
   * e.g. https://www.essent.nl/isolatie#wow?param=1 -> https://www.essent.nl/isolatie
   * @returns document URL without fragments and query params.
   */
  public getUrlWithoutParamsAndFragments() {
    if (!this.location) {
      return '';
    }
    const { location } = this;
    return `${location.protocol}//${location.host}${location.pathname}`;
  }

  /**
   * transforms the document location to a path without query params and fragments.
   * e.g. https://www.essent.nl/isolatie#wow?param=1 -> /isolatie
   * @returns document path without fragments and query params.
   */
  public getPathWithoutParamsAndFragments() {
    if (!this.location) {
      return '';
    }
    return this.location.pathname;
  }

  /**
   * Generates an element placement string based on the provided element name.
   * element placement strings comply with the following spec:
   *
   * `<brand>|<environment> <segment>|<page title>|<elementName>`
   *
   * When visiting the homepage of essent.nl and calling this method without an element name
   * this would yield the following result:
   *
   * `"essent|open consument|homepage|unknown component"`
   *
   * @param elementName name indentifying the provided element. If not present
   * a fallback to 'unknown component' is made.
   * @returns promise containing element placement string based on the provided element name.
   */
  async getElementPlacement(elementName = 'unknown component') {
    return `${await this.getSiteContext()}|${elementName.toLowerCase()}`;
  }

  /**
   * Generates a site context string.
   *
   * `<brand>|<environment> <segment>|<page title>`
   *
   * When visiting the homepage of essent.nl and calling this method this would yield the following result:
   *
   * `"essent|open consument|homepage`
   *
   * @returns promise containing site context string.
   */
  getSiteContext() {
    const config = this.#config();
    return firstValueFrom(
      this.#store$.pipe(
        select(getPageTitle),
        filter((pageTitle): pageTitle is string => pageTitle != null),
        map((pageTitle: string) => {
          return `${config.brand}|${config.type} ${config.segment}|${pageTitle}`.toLowerCase();
        }),
        take(1)
      )
    );
  }

  /**
   * Prefixes the given value with the site context abbreviation.
   * @param value string to be prefixed.
   * @returns Value prefixced with an abbreviation for the site context, e.g. 'ESC' for 'ESsent Consument'.
   * @example
   * ```typescript
   * prefixWithAbbreviation('element');
   * // return 'ESC element' on Essent Consumer
   * ```
   */
  prefixWithAbbreviation(value: string) {
    return `${this.getSiteContextAbbreviation()} ${value.toLowerCase()}`;
  }

  /**
   * Generates an abbreviaton based on the brand and segment of the website you're visiting.
   * @returns an abbreviation for the site context, e.g. 'ESC' for 'ESsent Consument'.
   */
  getSiteContextAbbreviation() {
    const config = this.#config();
    if (
      config.brand != null &&
      config.brand.length >= 2 &&
      config.segment != null &&
      config.segment.length >= 1
    ) {
      const brandAbbreviation = config.brand.substring(0, 2);
      const segmentAbbreviation = config.segment.substring(0, 1);
      return `${brandAbbreviation}${segmentAbbreviation}`.toUpperCase() as
        | 'ESC'
        | 'ESZ'
        | 'ENC';
    }
    return '';
  }

  /**
   * Generates a unique Analytics E2E ID by combining the site context abbreviation
   * with a 9-digit random number. This ID is typically used for analytics and tracking.
   *
   * This method is generally useful for when a 3rd party tool wants to have an identifier generated by
   * the application frontend that can be provided to both sales API's and tracking API's to have a shared
   * token to track a customer through a customer journey.
   * @returns {string} The generated Analytics E2E ID, formatted as `${context}_${id}`.
   */
  generateAnalyticsE2EId() {
    const charset = '0123456789'; // Character set used for generating the random ID (digits 0-9).

    // Get the context abbreviation for the site (e.g., "ESC").
    const context = this.getSiteContextAbbreviation();

    let id = ''; // Initialize an empty string to build the 9-digit ID.

    // Loop 9 times to generate a random 9-digit ID.
    for (let i = 0; i < 9; i++) {
      id += charset.charAt(Math.floor(Math.random() * charset.length)); // Append a random digit.
    }

    return `${context}_${id}`;
  }
}
